import { useTranslation } from 'react-i18next'
import { Clock, AlertCircle } from 'lucide-react'
import { HistoryTimeline } from '../../../components/history/HistoryTimeline'
import { useHistory } from '../../../hooks/use-history'
import { useHistoryStats } from '../../../hooks/use-history-stats'
import { useHistoryExport } from '../../../hooks/use-history-export'
import type { ExportOptions, HistoryStats } from '../../../types/history'

interface HistoryTabProps {
  caseId: string
  caseName: string
}

const DEFAULT_STATS: HistoryStats = {
  totalEntries: 0,
  entriesToday: 0,
  entriesThisWeek: 0,
  byCategory: {
    view: 0,
    analysis: 0,
    finding: 0,
    decision: 0,
    edit: 0,
    annotation: 0,
    export: 0,
    config: 0,
    collaboration: 0,
  },
  byActorType: { user: 0, ai: 0, system: 0 },
  bySection: {
    'case-management': 0,
    infrastructure: 0,
    'evidence-analysis': 0,
    'ai-config': 0,
    history: 0,
    visualization: 0,
    reporting: 0,
  },
  memoriesCreated: 0,
  verificationStatus: 'pending',
}

function LoadingState({ message }: { message: string }): React.ReactElement {
  return (
    <div className="flex items-center justify-center py-16">
      <Clock className="h-6 w-6 animate-spin text-slate-400" />
      <span className="ml-3 text-slate-600 dark:text-slate-400">{message}</span>
    </div>
  )
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string
  onRetry: () => void
}): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16">
      <AlertCircle className="h-8 w-8 text-red-400" />
      <p className="text-slate-600 dark:text-slate-400">{message}</p>
      <button
        onClick={onRetry}
        className="rounded-lg bg-[var(--accent,#4e5e82)] px-4 py-2 text-sm text-white transition-colors hover:opacity-90"
      >
        Retry
      </button>
    </div>
  )
}

function EmptyState({ message }: { message: string }): React.ReactElement {
  return (
    <div className="py-16 text-center">
      <Clock className="mx-auto h-12 w-12 text-slate-300 dark:text-slate-600" />
      <p className="mt-4 text-slate-600 dark:text-slate-400">{message}</p>
    </div>
  )
}

export function HistoryTab({ caseId, caseName }: HistoryTabProps): React.ReactElement {
  const { t } = useTranslation('history')
  const { entries, loading, error, refetch } = useHistory(caseId)
  const { stats, loading: statsLoading } = useHistoryStats(caseId)
  const { exportHistory } = useHistoryExport(caseId)

  const handleExport = (format: string): void => {
    const options: ExportOptions = {
      format: format as 'pdf' | 'json' | 'csv',
      includeHashes: true,
      includeMethodology: true,
      includeBeforeAfterStates: false,
    }
    void exportHistory(options)
  }

  if (loading || statsLoading) return <LoadingState message={t('loading')} />
  if (error) return <ErrorState message={t('error')} onRetry={() => void refetch()} />
  if (entries.length === 0) return <EmptyState message={t('empty')} />

  return (
    <HistoryTimeline
      entries={entries}
      stats={stats ?? DEFAULT_STATS}
      caseName={caseName}
      onExport={handleExport}
    />
  )
}

export default HistoryTab
